import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect } from "@/components/ui/multi-select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ScheduleInterviewDialog } from "./ScheduleInterviewDialog";
import { atticaMunicipalities } from "./atticaMunicipalities";
import { useCandidateDraft } from "@/hooks/useCandidateDraft";

interface AddCandidateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCandidateAdded?: () => void;
}

export function AddCandidateDialog({ open, onOpenChange, onCandidateAdded }: AddCandidateDialogProps) {
  const [loading, setLoading] = useState(false);
  const [scheduleFirstInterview, setScheduleFirstInterview] = useState(false);
  const [scheduleSecondInterview, setScheduleSecondInterview] = useState(false);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [newCandidateId, setNewCandidateId] = useState<string | null>(null);
  const [newCandidateName, setNewCandidateName] = useState<string>("");
  const [scheduleType, setScheduleType] = useState<'first' | 'second'>('first');
  const { toast } = useToast();
  
  // Use secure database-backed draft instead of localStorage
  const { draft, saveDraft, deleteDraft, isSaving } = useCandidateDraft('add_candidate');
  
  const [formData, setFormData] = useState(() => {
    // Load from secure database draft
    if (draft) {
      return draft;
    }
    
    // Default values
    return {
      full_name: "",
      email: "",
      phone: "",
      date_of_birth: "",
      identity_number: "",
      notes: "",
      suburbs: [] as string[]
    };
  });

  // Save to database whenever formData changes (debounced via hook)
  useEffect(() => {
    if (open && Object.values(formData).some(v => Array.isArray(v) ? v.length > 0 : v !== "")) {
      saveDraft(formData);
    }
  }, [formData, open]);

// Δήμοι Αττικής (shared list)
// Χρησιμοποιούμε το atticaMunicipalities από './atticaMunicipalities'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Get current user ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Only include fields that exist in the candidates table
      const candidateData = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        date_of_birth: formData.date_of_birth || null,
        identity_number: formData.identity_number,
        notes: formData.notes,
        suburbs: formData.suburbs,
        status: 'applied',
        created_by: user.id
      };

      const { data, error } = await supabase
        .from('candidates')
        .insert([candidateData])
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Επιτυχία",
        description: "Ο υποψήφιος προστέθηκε επιτυχώς",
      });

      // Notify parent component to refresh data
      onCandidateAdded?.();

      // Store candidate info for potential interview scheduling
      setNewCandidateId(data.id);
      setNewCandidateName(formData.full_name);

      // Check if we need to schedule interviews
      if (scheduleFirstInterview) {
        setScheduleType('first');
        setShowScheduleDialog(true);
      } else if (scheduleSecondInterview) {
        setScheduleType('second');
        setShowScheduleDialog(true);
      } else {
        // Reset form and close if no interviews to schedule
        resetForm();
        onOpenChange(false);
      }
    } catch (error: any) {
      toast({
        title: "Σφάλμα",
        description: error.message || "Αποτυχία προσθήκης υποψηφίου",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    const defaultData = {
      full_name: "",
      email: "",
      phone: "",
      date_of_birth: "",
      identity_number: "",
      notes: "",
      suburbs: []
    };
    
    setFormData(defaultData);
    setScheduleFirstInterview(false);
    setScheduleSecondInterview(false);
    setNewCandidateId(null);
    setNewCandidateName("");
    
    // Clear saved data after successful submission
    deleteDraft();
  };

  const handleScheduleDialogClose = () => {
    setShowScheduleDialog(false);
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Προσθήκη Νέου Υποψηφίου</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Πλήρες Όνομα *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => handleInputChange('full_name', e.target.value)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Τηλέφωνο</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="identity_number">Αριθμός Ταυτότητας</Label>
              <Input
                id="identity_number"
                value={formData.identity_number}
                onChange={(e) => handleInputChange('identity_number', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_of_birth">Ημερομηνία Γέννησης</Label>
            <Input
              id="date_of_birth"
              type="date"
              value={formData.date_of_birth}
              onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="suburbs">Δήμοι Αττικής</Label>
            <MultiSelect
              options={atticaMunicipalities}
              selected={formData.suburbs}
              onChange={(selected) => setFormData(prev => ({ ...prev, suburbs: selected }))}
              placeholder="Επιλέξτε δήμους όπου είναι διαθέσιμος ο υποψήφιος..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Σημειώσεις</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Επιπλέον σημειώσεις για τον υποψήφιο..."
              rows={3}
            />
          </div>

          {/* Interview Scheduling Options */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
            <Label className="text-sm font-medium">Προγραμματισμός Συνεντεύξεων (Προαιρετικό)</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="schedule-first"
                  checked={scheduleFirstInterview}
                  onCheckedChange={(checked) => setScheduleFirstInterview(checked as boolean)}
                />
                <Label htmlFor="schedule-first" className="text-sm">
                  Προγραμματισμός Πρώτης Συνέντευξης άμεσα
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="schedule-second"
                  checked={scheduleSecondInterview}
                  onCheckedChange={(checked) => setScheduleSecondInterview(checked as boolean)}
                />
                <Label htmlFor="schedule-second" className="text-sm">
                  Προγραμματισμός Δεύτερης Συνέντευξης άμεσα
                </Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Ακύρωση
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Προσθήκη..." : (scheduleFirstInterview || scheduleSecondInterview ? "Προσθήκη & Προγραμματισμός" : "Προσθήκη Υποψηφίου")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Schedule Interview Dialog */}
      <ScheduleInterviewDialog
        open={showScheduleDialog}
        onOpenChange={handleScheduleDialogClose}
        candidateId={newCandidateId || undefined}
        candidateName={newCandidateName}
        interviewType={scheduleType}
      />
    </Dialog>
  );
}